import { convertLength, FormField, ModalDialog, QuestionMarkIcon, Select } from "@rothoblaas/design-system";
import classNames from "classnames";
import { memo, useEffect, useMemo, useState, type FC } from "react";
import { Controller, useFormContext, useFormState, useWatch } from "react-hook-form";
import FormFieldsContainer from "../../../components/ui/FormFieldsContainer";
import Icon from "../../../components/ui/Icon";
import { useReadOnly } from "../../../components/ui/ReadOnlyGate";
import useI18n from "../../../hooks/useTranslations";
import { RECTANGULAR_SHAPE } from "../../../http/deckDesigner/types/configurations";
import type { ConnectorMappingForSettings } from "../../../http/deckDesigner/types/connectorMappings";
import {
  FASTENING_TYPE_CODE_INVISIBLE_FASTENING,
  FASTENING_TYPE_CODE_VISIBLE_FASTENING,
  type FasteningTypeForSettings,
} from "../../../http/deckDesigner/types/fasteningTypes";
import {
  GROOVING_TYPE_CODE_ASYMMETRICAL_GROOVING,
  GROOVING_TYPE_CODE_SYMMETRICAL_GROOVING,
  GROOVING_TYPE_CODE_WITHOUT_GROOVING,
} from "../../../http/deckDesigner/types/groovingTypes";
import type { Settings } from "../../../http/deckDesigner/types/settings";
import type { DeckDesignerConfigurationFormValues } from "../../../http/deckDesigner/useAutoSaveDeckDesignerConfiguration";
import { getOptionImageSrc } from "./optionImage";
import {
  getAsymmetricalGroovingMinimumBoardThickness,
  MIN_BOARD_THICKNESS_BY_GROOVING_TYPE_MM,
} from "./boardThicknessConstants";
import styles from "./BoardsCard.module.css";
import { filterConnectorMappings } from "./connectorMappingFilter";
import LengthInput from "./LengthInput";
import { useMeasurementFormat } from "./measurementFormat";
import OptionsStatus from "./OptionsStatus";
import {
  getSingleOptionAutoSelectSignature,
  useSingleOptionAutoSelect,
} from "../../../hooks/useSingleOptionAutoSelect";

const toIntegerValue = (value: number | null): number | null => (value == null ? null : Math.round(value));

const isPositiveNumber = (value: number | null | undefined): value is number =>
  typeof value === "number" && Number.isFinite(value) && value > 0;

type GapOption = { value: number };

const getSingleGap = (connectorMappings: ConnectorMappingForSettings[]): number | null => {
  const firstConnectorMapping = connectorMappings[0];

  if (!firstConnectorMapping || firstConnectorMapping.minimumGap !== firstConnectorMapping.maximumGap) {
    return null;
  }

  const firstGap = firstConnectorMapping.minimumGap;

  return connectorMappings.every((option) => option.minimumGap === firstGap && option.maximumGap === firstGap)
    ? firstGap
    : null;
};

const getGapRange = (connectorMappings: ConnectorMappingForSettings[]): { minimum: number; maximum: number } | null => {
  const firstConnectorMapping = connectorMappings[0];

  if (!firstConnectorMapping) {
    return null;
  }

  return connectorMappings.reduce(
    (range, option) => ({
      minimum: Math.min(range.minimum, option.minimumGap),
      maximum: Math.max(range.maximum, option.maximumGap),
    }),
    {
      minimum: firstConnectorMapping.minimumGap,
      maximum: firstConnectorMapping.maximumGap,
    },
  );
};

const getGapOptions = (connectorMappings: ConnectorMappingForSettings[]): GapOption[] => {
  const values = new Set<number>();

  connectorMappings.forEach((option) => {
    const minimum = Math.ceil(option.minimumGap);
    const maximum = Math.floor(option.maximumGap);

    for (let value = minimum; value <= maximum; value += 1) {
      values.add(value);
    }
  });

  return Array.from(values)
    .sort((left, right) => left - right)
    .map((value) => ({ value }));
};

type GroovingHeightOption = { value: number };

const getGroovableConnectorMappings = (
  connectorMappings: ConnectorMappingForSettings[],
): ConnectorMappingForSettings[] =>
  connectorMappings.filter(
    (option) => option.connectorFamily.groovingMinHeight != null && option.connectorFamily.groovingMaxHeight != null,
  );

const getGroovingHeightRange = (
  connectorMappings: ConnectorMappingForSettings[],
): { minimum: number; maximum: number } | null => {
  const groovableConnectorMappings = getGroovableConnectorMappings(connectorMappings);
  const [firstConnectorMapping] = groovableConnectorMappings;

  if (!firstConnectorMapping) {
    return null;
  }

  return groovableConnectorMappings.reduce(
    (range, option) => ({
      minimum: Math.min(range.minimum, option.connectorFamily.groovingMinHeight!),
      maximum: Math.max(range.maximum, option.connectorFamily.groovingMaxHeight!),
    }),
    {
      minimum: firstConnectorMapping.connectorFamily.groovingMinHeight!,
      maximum: firstConnectorMapping.connectorFamily.groovingMaxHeight!,
    },
  );
};

const getGroovingHeightOptions = (connectorMappings: ConnectorMappingForSettings[]): GroovingHeightOption[] => {
  const values = new Set<number>();

  getGroovableConnectorMappings(connectorMappings).forEach((option) => {
    const minimum = Math.ceil(option.connectorFamily.groovingMinHeight!);
    const maximum = Math.floor(option.connectorFamily.groovingMaxHeight!);

    for (let value = minimum; value <= maximum; value += 1) {
      values.add(value);
    }
  });

  return Array.from(values)
    .sort((left, right) => left - right)
    .map((value) => ({ value }));
};

type BoardsCardProps = {
  defaultValues: DeckDesignerConfigurationFormValues;
  settings: Settings | undefined;
  hasError: boolean | undefined;
};

const ERROR_FIELD_NAMES = ["gap", "groovingHeight", "boardWidth", "boardThickness"] as const;

const BoardsCard: FC<BoardsCardProps> = ({ defaultValues, settings, hasError }) => {
  const i18n = useI18n();
  const isReadOnly = useReadOnly();
  const calculationTranslation = i18n.translation.deckDesignerModule.configurations.calculation;
  const fasteningTypes = settings?.fasteningTypes;
  const connectorMappings = settings?.connectorMappings;

  const directionOptions = useMemo(
    () =>
      [
        {
          id: "horizontal",
          value: true,
          image: "/deckDesigner/horizontal.png",
          name: calculationTranslation.horizontal,
        },
        {
          id: "vertical",
          value: false,
          image: "/deckDesigner/vertical.png",
          name: calculationTranslation.vertical,
        },
      ] as const,
    [calculationTranslation],
  );

  const fasteningTypeDescriptionByCode = useMemo<Record<string, string>>(
    () => ({
      [FASTENING_TYPE_CODE_VISIBLE_FASTENING]: calculationTranslation.visibleFasteningDescription,
      [FASTENING_TYPE_CODE_INVISIBLE_FASTENING]: calculationTranslation.hiddenFasteningDescription,
    }),
    [calculationTranslation],
  );

  const [helpFasteningType, setHelpFasteningType] = useState<FasteningTypeForSettings | null>(null);

  const { control, setValue, trigger } = useFormContext<DeckDesignerConfigurationFormValues>();
  // Scoped to this card's own fields: reading formState from the context subscribes the whole
  // form (useForm) instead, re-rendering every card on any validation.
  const { errors } = useFormState({ control, name: ERROR_FIELD_NAMES });

  const fasteningType = useWatch({ control, name: "fasteningType", exact: true });
  const groovingType = useWatch({ control, name: "groovingType", exact: true });
  const shape = useWatch({ control, name: "shape", exact: true });
  const base = useWatch({ control, name: "base", exact: true });
  const height = useWatch({ control, name: "height", exact: true });
  const secondaryBase = useWatch({ control, name: "secondaryBase", exact: true });
  const secondaryHeight = useWatch({ control, name: "secondaryHeight", exact: true });
  const hasHorizontalBoards = useWatch({ control, name: "hasHorizontalBoards", exact: true });
  const gap = useWatch({ control, name: "gap", exact: true });
  const connectorFamily = useWatch({ control, name: "connectorFamily", exact: true });
  const connector = useWatch({ control, name: "connector", exact: true });
  const boardWidth = useWatch({ control, name: "boardWidth", exact: true });
  const boardThickness = useWatch({ control, name: "boardThickness", exact: true });
  const measurementFormat = useMeasurementFormat();
  const isImperial = measurementFormat.isImperial;
  const formatMm = (value: number) =>
    measurementFormat.length(value, "mm", `${value} ${i18n.translation.common.unitMm}`);
  // Gap and grooving height: inches with 2 decimals instead of fractions, as requested.
  const formatMmAsDecimalInches = (value: number) =>
    isImperial ? `${convertLength(value, "mm", "in").toFixed(2)}"` : formatMm(value);
  const groovingHeight = useWatch({ control, name: "groovingHeight", exact: true });
  const { rememberSingleOptionChange, shouldAutoSelectSingleOption } = useSingleOptionAutoSelect();
  const selectedConnectorFamily = connectorFamily ?? connector?.connectorFamily ?? null;
  const isSymmetricalGrooving = groovingType?.code === GROOVING_TYPE_CODE_SYMMETRICAL_GROOVING;
  const isAsymmetricalGrooving = groovingType?.code === GROOVING_TYPE_CODE_ASYMMETRICAL_GROOVING;
  const hasNoGrooving = groovingType?.code === GROOVING_TYPE_CODE_WITHOUT_GROOVING;
  // Symmetrical grooving keeps the groove centered by construction, so its height is derived from
  // board thickness (see the effect below) instead of being a free choice like the asymmetrical case.
  const showGroovingHeight = !hasNoGrooving && !isSymmetricalGrooving;

  const connectorMappingsForSelectedData = useMemo(
    () =>
      connectorMappings
        ? filterConnectorMappings(connectorMappings, {
            fasteningType,
            groovingType,
          })
        : undefined,
    [connectorMappings, fasteningType, groovingType],
  );

  const selectedConnectorMappingsGapRange = useMemo(
    () => (connectorMappingsForSelectedData ? getGapRange(connectorMappingsForSelectedData) : null),
    [connectorMappingsForSelectedData],
  );

  const gapOptions = useMemo(
    () => (connectorMappingsForSelectedData ? getGapOptions(connectorMappingsForSelectedData) : undefined),
    [connectorMappingsForSelectedData],
  );

  const gapAutoSelectSignature = useMemo(
    () =>
      getSingleOptionAutoSelectSignature(
        gapOptions,
        (option) => `${option.value}`,
        connectorMappingsForSelectedData?.map((option) => option.id).join("|"),
      ),
    [connectorMappingsForSelectedData, gapOptions],
  );

  const gapRange = selectedConnectorMappingsGapRange;

  const groovingHeightRange = useMemo(
    () => (connectorMappingsForSelectedData ? getGroovingHeightRange(connectorMappingsForSelectedData) : null),
    [connectorMappingsForSelectedData],
  );

  const groovingHeightOptions = useMemo(
    () => (connectorMappingsForSelectedData ? getGroovingHeightOptions(connectorMappingsForSelectedData) : undefined),
    [connectorMappingsForSelectedData],
  );

  const groovingHeightAutoSelectSignature = useMemo(
    () =>
      getSingleOptionAutoSelectSignature(
        groovingHeightOptions,
        (option) => `${option.value}`,
        connectorMappingsForSelectedData?.map((option) => option.id).join("|"),
      ),
    [connectorMappingsForSelectedData, groovingHeightOptions],
  );

  useEffect(() => {
    trigger("boardThickness");
  }, [boardThickness, boardWidth, connector, connectorFamily, groovingHeight, groovingHeightRange, groovingType]);

  useEffect(() => {
    trigger("boardWidth");
  }, [base, hasHorizontalBoards, height, shape, secondaryBase, secondaryHeight]);

  useEffect(() => {
    trigger("gap");
  }, [gap, gapOptions, gapRange]);

  useEffect(() => {
    trigger("groovingHeight");
  }, [groovingHeight, groovingHeightOptions, groovingHeightRange, showGroovingHeight]);

  const validateBoardThickness = (value: number | null) => {
    if (value == null) {
      return true;
    }

    if (boardWidth != null && value >= boardWidth) {
      return calculationTranslation.boardThicknessMustNotExceedBoardWidth;
    }

    // Asymmetric grooving gets its own, tighter minimum below (derived from grooving height/family
    // rather than a single flat number) - it replaces the flat structural check rather than adding to
    // it, so only one "too thin" message shows at a time. The flat check still applies as a fallback
    // when there's no grooving height to even estimate from yet (e.g. settings still loading).
    let hasCheckedAsymmetricalGroovingMinimum = false;

    if (isAsymmetricalGrooving) {
      // Neither groovingHeight nor the connector family may be picked yet - fall back to the lowest
      // selectable grooving height, and to a conservative flat margin instead of the family's own
      // groovingThickness, so the field still enforces a real (if provisional) minimum that only
      // tightens as more of the configuration gets filled in.
      const effectiveGroovingHeight = groovingHeight ?? groovingHeightRange?.minimum ?? null;

      if (effectiveGroovingHeight != null) {
        hasCheckedAsymmetricalGroovingMinimum = true;

        const minimumBoardThickness = getAsymmetricalGroovingMinimumBoardThickness(
          effectiveGroovingHeight,
          selectedConnectorFamily?.groovingThickness,
        );

        if (value < minimumBoardThickness) {
          return calculationTranslation.boardThicknessBelowAsymmetricalGroovingMinimum(formatMm(minimumBoardThickness));
        }
      }
    }

    const minimumThicknessForGroovingType = groovingType
      ? MIN_BOARD_THICKNESS_BY_GROOVING_TYPE_MM[groovingType.code]
      : undefined;

    if (
      !hasCheckedAsymmetricalGroovingMinimum &&
      minimumThicknessForGroovingType != null &&
      value < minimumThicknessForGroovingType
    ) {
      return calculationTranslation.boardThicknessBelowGroovingMinimum(formatMm(minimumThicknessForGroovingType));
    }

    if (selectedConnectorFamily?.maxBoardThickness != null && value > selectedConnectorFamily.maxBoardThickness) {
      return calculationTranslation.boardThicknessExceedsConnectorFamilyMaxThickness(
        formatMm(selectedConnectorFamily.maxBoardThickness),
      );
    }

    if (
      isSymmetricalGrooving &&
      selectedConnectorFamily?.groovingThickness != null &&
      selectedConnectorFamily.groovingMinHeight != null &&
      selectedConnectorFamily.groovingMaxHeight != null
    ) {
      const minBoardThickness =
        selectedConnectorFamily.groovingMinHeight * 2 + selectedConnectorFamily.groovingThickness;
      const maxBoardThickness =
        selectedConnectorFamily.groovingMaxHeight * 2 + selectedConnectorFamily.groovingThickness;

      if (value < minBoardThickness || value > maxBoardThickness) {
        return calculationTranslation.boardThicknessOutOfSymmetricalGroovingRange(
          formatMm(minBoardThickness),
          formatMm(maxBoardThickness),
        );
      }
    }

    return true;
  };

  const validateBoardWidth = (value: number | null) => {
    if (value == null) {
      return true;
    }

    const dimensionValues =
      hasHorizontalBoards === false
        ? shape === RECTANGULAR_SHAPE
          ? [base]
          : [secondaryBase, isPositiveNumber(base) && isPositiveNumber(secondaryBase) ? base - secondaryBase : null]
        : shape === RECTANGULAR_SHAPE
          ? [height]
          : [
              secondaryHeight,
              isPositiveNumber(height) && isPositiveNumber(secondaryHeight) ? height - secondaryHeight : null,
            ];

    const roundedValue = Math.min(
      ...dimensionValues.filter(isPositiveNumber).map((dimensionValue) => Math.round(dimensionValue * 1000)),
    );
    const maxBoardWidth = shape === RECTANGULAR_SHAPE ? roundedValue / 2 : roundedValue;

    if (!Number.isFinite(maxBoardWidth) || value <= maxBoardWidth) {
      return true;
    }

    return calculationTranslation.boardWidthMustNotExceedDeckDimension(formatMm(maxBoardWidth));
  };

  const validateGap = (value: number | null) => {
    if (value == null) {
      return true;
    }

    if (!connectorMappingsForSelectedData) {
      return value >= 0 || i18n.translation.common.fieldContainsInvalidValueInsertBiggerOne;
    }

    if (gapOptions?.some((option) => option.value === value)) {
      return true;
    }

    if (gapRange && (value < gapRange.minimum || value > gapRange.maximum)) {
      return calculationTranslation.gapIncompatible(
        formatMmAsDecimalInches(gapRange.minimum),
        formatMmAsDecimalInches(gapRange.maximum),
      );
    }

    return i18n.translation.common.fieldInvalidValue;
  };

  const validateGroovingHeight = (value: number | null) => {
    if (value == null || !showGroovingHeight) {
      return true;
    }

    if (groovingHeightOptions?.some((option) => option.value === value)) {
      return true;
    }

    if (groovingHeightRange && (value < groovingHeightRange.minimum || value > groovingHeightRange.maximum)) {
      return calculationTranslation.groovingHeightOutOfConnectorFamilyRange(
        formatMmAsDecimalInches(groovingHeightRange.minimum),
        formatMmAsDecimalInches(groovingHeightRange.maximum),
      );
    }

    return i18n.translation.common.fieldInvalidValue;
  };

  useEffect(() => {
    if (hasHorizontalBoards == null) {
      setValue("hasHorizontalBoards", true);
    }
  }, [hasHorizontalBoards]);

  useEffect(() => {
    if (hasNoGrooving && groovingHeight != null) {
      setValue("groovingHeight", null, { shouldDirty: true, shouldValidate: true });
    }
  }, [groovingHeight, hasNoGrooving, setValue]);

  // A context change (e.g. gap changing which connector family gets auto-selected) can leave a
  // previously valid groovingHeight outside the new family's range - clear it so the auto-select
  // effect below can pick a fresh value instead of the stale one silently failing validation.
  useEffect(() => {
    if (
      !showGroovingHeight ||
      groovingHeight == null ||
      !groovingHeightOptions ||
      groovingHeightOptions.some((option) => option.value === groovingHeight)
    ) {
      return;
    }

    setValue("groovingHeight", null, { shouldDirty: true, shouldValidate: true });
  }, [groovingHeight, groovingHeightOptions, setValue, showGroovingHeight]);

  useEffect(() => {
    if (!isSymmetricalGrooving) {
      return;
    }

    // Nothing left to derive it from (e.g. the connector family got cleared) - clear the stale
    // value instead of leaving a derived-but-now-orphaned one around.
    if (boardThickness == null || selectedConnectorFamily?.groovingThickness == null) {
      if (groovingHeight != null) {
        setValue("groovingHeight", null, { shouldDirty: true, shouldValidate: true });
      }
      return;
    }

    // groovingHeight is stored as whole millimeters server-side - board thickness minus grooving
    // thickness isn't always even, so round the halved result. A board too thin for this family's
    // groove would derive a negative height, which the database rejects outright - clear it instead
    // so the real problem (board thickness) can surface through its own validation.
    const nextGroovingHeight = Math.round((boardThickness - selectedConnectorFamily.groovingThickness) / 2);
    const resolvedGroovingHeight = nextGroovingHeight < 0 ? null : nextGroovingHeight;

    if (resolvedGroovingHeight !== groovingHeight) {
      setValue("groovingHeight", resolvedGroovingHeight, { shouldDirty: true, shouldValidate: true });
    }
  }, [boardThickness, groovingHeight, isSymmetricalGrooving, selectedConnectorFamily?.groovingThickness, setValue]);

  useEffect(() => {
    const firstFasteningType = fasteningTypes?.[0];

    if (fasteningType || defaultValues.fasteningType || !firstFasteningType) {
      return;
    }

    setValue("fasteningType", firstFasteningType);
    setValue("groovingType", firstFasteningType.groovingTypes[0] ?? null);
  }, [defaultValues.fasteningType, fasteningType, fasteningTypes]);

  useEffect(() => {
    if (
      gap != null ||
      !gapOptions?.length ||
      !connectorMappingsForSelectedData ||
      !shouldAutoSelectSingleOption("gap", gapAutoSelectSignature)
    ) {
      return;
    }

    const [firstGapOption] = gapOptions;
    const singleGap =
      gapOptions.length === 1 && firstGapOption ? firstGapOption.value : getSingleGap(connectorMappingsForSelectedData);

    if (singleGap != null) {
      setValue("gap", Math.round(singleGap));
    }
  }, [
    connectorMappingsForSelectedData,
    gap,
    gapAutoSelectSignature,
    gapOptions,
    shouldAutoSelectSingleOption,
    setValue,
  ]);

  useEffect(() => {
    if (
      !showGroovingHeight ||
      groovingHeight != null ||
      groovingHeightOptions?.length !== 1 ||
      !shouldAutoSelectSingleOption("groovingHeight", groovingHeightAutoSelectSignature)
    ) {
      return;
    }

    const [singleGroovingHeightOption] = groovingHeightOptions;

    if (singleGroovingHeightOption) {
      setValue("groovingHeight", singleGroovingHeightOption.value, { shouldDirty: true, shouldValidate: true });
    }
  }, [
    groovingHeight,
    groovingHeightAutoSelectSignature,
    groovingHeightOptions,
    shouldAutoSelectSingleOption,
    showGroovingHeight,
    setValue,
  ]);

  return (
    <div>
      <FormFieldsContainer fullWidth>
        <Controller
          name="hasHorizontalBoards"
          control={control}
          defaultValue={defaultValues.hasHorizontalBoards}
          render={({ field }) => {
            const selectedDirection = field.value === false ? "vertical" : "horizontal";

            return (
              <FormField label={calculationTranslation.boardDirection}>
                <div className={styles.grid}>
                  {directionOptions.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      className={classNames(styles.option, {
                        [styles.selected]: selectedDirection === option.id,
                      })}
                      aria-pressed={selectedDirection === option.id}
                      onClick={() => field.onChange(option.value)}
                    >
                      <img className={styles.image} src={option.image} alt={calculationTranslation[option.id]} />
                      <span className={styles.label}>{option.name}</span>
                    </button>
                  ))}
                </div>
              </FormField>
            );
          }}
        />

        <Controller
          name="fasteningType"
          control={control}
          defaultValue={defaultValues.fasteningType}
          render={({ field }) => (
            <FormField label={calculationTranslation.fasteningType}>
              <div className={styles.grid}>
                <OptionsStatus
                  className={styles.option}
                  isLoading={!hasError && !fasteningTypes}
                  hasError={!!hasError}
                />
                {fasteningTypes?.map((option) => {
                  const label = option.name;
                  const isSelected = field.value?.code === option.code;
                  const description = fasteningTypeDescriptionByCode[option.code];

                  return (
                    <div key={option.code} className={styles.optionWrapper}>
                      <button
                        type="button"
                        className={classNames(styles.option, styles.optionCompact, {
                          [styles.selected]: isSelected,
                        })}
                        aria-pressed={isSelected}
                        onClick={() => {
                          if (isSelected) return;

                          const nextGroovingType = option.groovingTypes[0] ?? null;

                          field.onChange(option);
                          setValue("groovingType", nextGroovingType);
                        }}
                      >
                        <span className={styles.label}>{label}</span>
                      </button>
                      {description && (
                        <button
                          type="button"
                          className={styles.help}
                          aria-label={i18n.translation.common.help}
                          onClick={(event) => {
                            event.stopPropagation();
                            setHelpFasteningType(option);
                          }}
                        >
                          <Icon glyph={QuestionMarkIcon} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </FormField>
          )}
        />

        <Controller
          name="groovingType"
          control={control}
          defaultValue={defaultValues.groovingType}
          render={({ field }) => (
            <FormField label={calculationTranslation.groovingType}>
              <div className={styles.grid}>
                <OptionsStatus
                  className={styles.option}
                  isLoading={!hasError && !fasteningType}
                  hasError={!!hasError}
                />
                {fasteningType?.groovingTypes?.map((option) => {
                  const label = option.name;
                  const isSelected = field.value?.code === option.code;

                  return (
                    <button
                      key={option.code}
                      type="button"
                      className={classNames(styles.option, {
                        [styles.selected]: isSelected,
                      })}
                      aria-pressed={isSelected}
                      onClick={() => field.onChange(option)}
                    >
                      <img className={styles.image} src={getOptionImageSrc(option.image ?? "")} alt={label} />

                      <span className={styles.label}>{label}</span>
                    </button>
                  );
                })}
              </div>
            </FormField>
          )}
        />

        <Controller
          name={"gap"}
          control={control}
          defaultValue={defaultValues.gap}
          rules={{ validate: validateGap }}
          render={({ field }) => (
            <FormField
              label={calculationTranslation.gap}
              error={errors.gap}
              info={
                gapOptions?.length === 1 && field.value != null
                  ? i18n.translation.common.singleOptionAvailable
                  : undefined
              }
            >
              {({ labelId, isInvalid }) => (
                <Select<GapOption, false>
                  {...field}
                  inputId={labelId}
                  value={gapOptions?.find((option) => option.value === field.value) ?? null}
                  options={gapOptions ?? []}
                  getOptionValue={(option) => `${option.value}`}
                  getOptionLabel={(option) => formatMmAsDecimalInches(option.value)}
                  placeholder={`${i18n.translation.common.select}...`}
                  isClearable
                  isLoading={!hasError && !connectorMappings}
                  isDisabled={isReadOnly || (gapOptions?.length === 1 && field.value != null)}
                  isInvalid={isInvalid}
                  onChange={(selectedOption) => {
                    const nextGap = selectedOption?.value ?? null;

                    rememberSingleOptionChange("gap", gapAutoSelectSignature, nextGap);
                    field.onChange(nextGap);
                  }}
                />
              )}
            </FormField>
          )}
        />

        {showGroovingHeight && (
          <Controller
            name={"groovingHeight"}
            control={control}
            defaultValue={defaultValues.groovingHeight}
            rules={{ validate: validateGroovingHeight }}
            render={({ field }) => (
              <FormField
                label={calculationTranslation.groovingHeight}
                error={errors.groovingHeight}
                info={
                  groovingHeightOptions?.length === 1 && field.value != null
                    ? i18n.translation.common.singleOptionAvailable
                    : undefined
                }
              >
                {({ labelId, isInvalid }) => (
                  <Select<GroovingHeightOption, false>
                    {...field}
                    inputId={labelId}
                    value={groovingHeightOptions?.find((option) => option.value === field.value) ?? null}
                    options={groovingHeightOptions ?? []}
                    getOptionValue={(option) => `${option.value}`}
                    getOptionLabel={(option) => formatMmAsDecimalInches(option.value)}
                    placeholder={`${i18n.translation.common.select}...`}
                    isClearable
                    isLoading={!hasError && !connectorMappings}
                    isDisabled={isReadOnly || (groovingHeightOptions?.length === 1 && field.value != null)}
                    isInvalid={isInvalid}
                    onChange={(selectedOption) => {
                      const nextGroovingHeight = selectedOption?.value ?? null;

                      rememberSingleOptionChange(
                        "groovingHeight",
                        groovingHeightAutoSelectSignature,
                        nextGroovingHeight,
                      );
                      field.onChange(nextGroovingHeight);
                    }}
                  />
                )}
              </FormField>
            )}
          />
        )}

        <Controller
          name={"boardWidth"}
          control={control}
          defaultValue={defaultValues.boardWidth}
          rules={{ min: 0, validate: validateBoardWidth }}
          render={({ field }) => (
            <FormField label={calculationTranslation.boardWidth} error={errors.boardWidth}>
              {({ labelId, isInvalid }) => (
                <LengthInput
                  {...field}
                  id={labelId}
                  isImperial={isImperial}
                  metricUnit="mm"
                  imperialUnit="in"
                  decimalScale={0}
                  suffix={` ${i18n.translation.common.unitMm}`}
                  allowNegative={false}
                  isInvalid={isInvalid}
                  onValueChange={(value) => field.onChange(toIntegerValue(value))}
                />
              )}
            </FormField>
          )}
        />

        <Controller
          name={"boardThickness"}
          control={control}
          defaultValue={defaultValues.boardThickness}
          rules={{ min: 0, validate: validateBoardThickness }}
          render={({ field }) => (
            <FormField label={calculationTranslation.boardThickness} error={errors.boardThickness}>
              {({ labelId, isInvalid }) => (
                <LengthInput
                  {...field}
                  id={labelId}
                  isImperial={isImperial}
                  metricUnit="mm"
                  imperialUnit="in"
                  decimalScale={0}
                  suffix={` ${i18n.translation.common.unitMm}`}
                  allowNegative={false}
                  isInvalid={isInvalid}
                  onValueChange={(value) => field.onChange(toIntegerValue(value))}
                />
              )}
            </FormField>
          )}
        />
      </FormFieldsContainer>

      {helpFasteningType && (
        <ModalDialog onClose={() => setHelpFasteningType(null)} fitContent>
          <p>{fasteningTypeDescriptionByCode[helpFasteningType.code]}</p>
        </ModalDialog>
      )}
    </div>
  );
};

// Memoized: its props are stable, so it re-renders through its own watched fields and props only, not
// whenever its parent does.
export default memo(BoardsCard);
